import { ColDef, ICellRendererParams } from 'ag-grid-community'
import clsx from 'clsx'
import { useWatch } from 'react-hook-form'

import { ApiDashboardAccount } from '@/api'
import { ColDefWithLoading, Icon, Link } from '@/components/core-v2'
import { PATH } from '@/const'
import { Currency, formatCurrencyFull } from '@/utilities/currency'

import { FilteredDashboardAccount } from '../../utils/useFilteredNotes'
import { TRADES_FILTERS_FIELDS } from '../trades-filters'
import classes from './index.module.scss'
import { Last30DaysChart } from './Last30DaysChart'
import { Last30DaysLoadingCell, NameLoadingCell } from './Loading'

export const DEFAULT_COLUMN_OPTIONS: ColDef = {
  sortable: true,
  unSortIcon: true,
  wrapText: false,
  autoHeight: true
}

const NameCell = ({
  data: { accountId, accountName, accountSource }
}: ICellRendererParams<ApiDashboardAccount>) => {
  const watchedValues = useWatch({
    name: TRADES_FILTERS_FIELDS
  })
  const formValues = watchedValues.reduce((acc, value, index) => {
    acc[TRADES_FILTERS_FIELDS[index]] = value
    return acc
  }, {})

  return (
    <div className={classes.nameCell}>
      <Link
        className="mb-half"
        state={{
          tradeFilterFormValues: formValues
        }}
        title={accountName}
        to={PATH.reportsSingleTradePerformance(accountSource, accountId)}
      >
        {accountName}
      </Link>
      <span title={accountId}>{accountId}</span>
    </div>
  )
}

const StrategyCell = ({
  data: { strategyTemplateName }
}: ICellRendererParams<ApiDashboardAccount>) => <span>{strategyTemplateName}</span>

const ValueCell = ({
  data: { accountValue, notionalCurrency }
}: ICellRendererParams<ApiDashboardAccount>) => {
  const value = formatCurrencyFull(accountValue, notionalCurrency as Currency)
  return <span title={value}>{value}</span>
}

const Last30DaysCell = ({
  data: { last30DaysPeformancePercent, last30DaysPerformance, status }
}: ICellRendererParams<ApiDashboardAccount>) => {
  const isPositive = last30DaysPeformancePercent >= 0

  if (status === 'Closed') {
    return (
      <div className={clsx(classes.last30DaysCell, classes.last30DaysCellClosed)}>
        <span className={classes.hyphen}>–</span>
        <div className={classes.noRecentActivity}>No recent activity</div>
      </div>
    )
  }

  const chartData = last30DaysPerformance.map(
    day => [new Date(day.date).getTime(), day.value] as [number, number]
  )

  return (
    <div
      className={clsx(classes.last30DaysCell, {
        positive: isPositive,
        negative: !isPositive
      })}
    >
      <span className="mr-2">
        {isPositive && '+'}
        {`${last30DaysPeformancePercent.toFixed(2)}%`}
      </span>
      <div>
        <Last30DaysChart data={chartData} isPositive={isPositive} />
      </div>
    </div>
  )
}

const STATUS_NAME_MAP = {
  New: 'Active',
  Running: 'Active'
}
const StatusCell = ({
  context: { collateralUser },
  data: {
    currentlyApproving,
    currentlyClosing,
    currentlyUpdating,
    isCollateralForLoan,
    isLockedForUpdates,
    status
  }
}: ICellRendererParams<FilteredDashboardAccount>) => (
  <span className={clsx(classes.statusCell, { [classes.closed]: status === 'Closed' })}>
    {(currentlyApproving || isLockedForUpdates || (!collateralUser && isCollateralForLoan)) && (
      <Icon name="padlock" />
    )}
    {currentlyClosing && <Icon name="remove" />}
    {currentlyUpdating && <Icon name="squareInput" />}
    {STATUS_NAME_MAP[status] ?? status}
  </span>
)

export const COLUMN_DEFS: ColDefWithLoading[] = [
  {
    headerName: 'Name',
    field: 'accountName',
    cellRenderer: NameCell,
    minWidth: 260,
    flex: 0.4,
    sort: 'desc',
    loadingCellRenderer: NameLoadingCell
  },
  { headerName: 'Note', field: 'noteName', minWidth: 180, flex: 0.17, loadingCellWidth: 'lg' },
  {
    headerName: 'Currency',
    field: 'notionalCurrency',
    minWidth: 114,
    flex: 0.04,
    type: 'rightAligned',
    loadingCellWidth: 'sm'
  },
  {
    headerName: 'Portfolio',
    field: 'portfolioName',
    minWidth: 180,
    flex: 0.085,
    loadingCellWidth: 'md'
  },
  {
    headerName: 'Strategy',
    field: 'strategyTemplateName',
    minWidth: 106,
    flex: 0.085,
    cellRenderer: StrategyCell,
    loadingCellWidth: 'md'
  },
  {
    headerName: 'Value',
    field: 'value',
    minWidth: 130,
    flex: 0.085,
    type: 'rightAligned',
    cellRenderer: ValueCell,
    loadingCellWidth: 'md'
  },
  {
    headerName: 'Last 30 days',
    field: 'last30DaysPercentage',
    minWidth: 300,
    flex: 0.2,
    type: 'rightAligned',
    cellRenderer: Last30DaysCell,
    loadingCellRenderer: Last30DaysLoadingCell
  },
  {
    headerName: 'Status',
    field: 'status',
    minWidth: 108,
    flex: 0.057,
    type: 'rightAligned',
    cellRenderer: StatusCell,
    loadingCellWidth: 'lg'
  }
]
